import React from "react";
import { Button, Tag } from "@jem-open/jem-ui";
import { AlertCircle } from "lucide-react";
import McmSegment from "../services/model/McmSegment";
import MultimessagingBreadcrumb from "../elements/MultimessagingBreadcrumb";

type SegmentDetailViewProps = {
  segment: McmSegment;
  onGoBack: () => void;
};

const SegmentDetailView: React.FC<SegmentDetailViewProps> = ({ segment, onGoBack }) => (
  <div>
    <MultimessagingBreadcrumb
      items={[
        { label: "Manage Segments", onClick: onGoBack },
        { label: segment.name },
      ]}
    />

    <h2 className="text-xl font-heading font-semibold text-greyscale-text-title mb-lg">
      Segment details
    </h2>

    <div className="flex flex-col gap-lg max-w-[850px]">
      {/* Target company */}
      {segment.target_company && (
        <div>
          <p className="text-sm font-semibold text-greyscale-text-title">Target Company</p>
          <p className="text-sm text-greyscale-text-caption mt-xxxs">
            {segment.target_company.company_name}
          </p>
        </div>
      )}

      {/* Segment name */}
      <div>
        <p className="text-sm font-semibold text-greyscale-text-title">Segment name</p>
        <p className="text-sm text-greyscale-text-caption mt-xxxs">
          {segment.name}
        </p>
      </div>

      {/* Description */}
      <div>
        <p className="text-sm font-semibold text-greyscale-text-title">Description</p>
        <p className="text-sm text-greyscale-text-caption mt-xxxs">
          {segment.description || "\u2014"}
        </p>
      </div>

      {/* Rules criteria */}
      <div>
        <h3 className="text-lg font-heading font-semibold text-greyscale-text-title mb-sm">
          Rules criteria ({segment.logic_operator})
        </h3>
        <div className="grid grid-cols-3 gap-xs">
          <span className="text-sm font-semibold text-greyscale-text-title">Field</span>
          <span className="text-sm font-semibold text-greyscale-text-title">Rule</span>
          <span className="text-sm font-semibold text-greyscale-text-title">Value</span>
        </div>
        {segment.rules.map((rule, index) => (
          // eslint-disable-next-line react/no-array-index-key -- read-only list, rules may have duplicate field/operator/value
          <div key={`rule-${index}-${rule.field}-${rule.operator}`} className="grid grid-cols-3 gap-xs mt-xxxs">
            <span className="text-sm text-greyscale-text-caption">{rule.field}</span>
            <span className="text-sm text-greyscale-text-caption">{rule.operator}</span>
            <span className="text-sm text-greyscale-text-caption">{String(rule.value)}</span>
          </div>
        ))}
        {segment.rules.length === 0 && (
          <p className="text-sm text-greyscale-text-caption mt-xxxs">No rules defined.</p>
        )}
      </div>

      {/* Date created */}
      <div>
        <p className="text-sm font-semibold text-greyscale-text-title">Date created</p>
        <p className="text-sm text-greyscale-text-caption mt-xxxs">
          {segment.created_at ? new Date(segment.created_at).toLocaleString() : "\u2014"}
        </p>
      </div>

      {/* Created by */}
      <div>
        <p className="text-sm font-semibold text-greyscale-text-title">Created by (User)</p>
        <p className="text-sm text-greyscale-text-caption mt-xxxs">
          {segment.created_by?.first_name && segment.created_by?.last_name
            ? `${segment.created_by.first_name} ${segment.created_by.last_name}`
            : "\u2014"}
        </p>
      </div>

      {/* Last updated */}
      <div>
        <p className="text-sm font-semibold text-greyscale-text-title">Last updated</p>
        <p className="text-sm text-greyscale-text-caption mt-xxxs">
          {segment.updated_at ? new Date(segment.updated_at).toLocaleString() : "\u2014"}
        </p>
      </div>

      {/* Segment status */}
      <div>
        <p className="text-sm font-semibold text-greyscale-text-title">Segment status</p>
        {segment.in_use ? (
          <>
            <p className="text-sm text-greyscale-text-caption mt-xxxs mb-xxs">
              This segment is currently active, scheduled, or drafted in a Multimessage announcement
            </p>
            <Tag variant="success">Currently in use</Tag>
          </>
        ) : (
          <>
            <p className="text-sm text-greyscale-text-caption mt-xxxs mb-xxs">
              This segment is not currently in use
            </p>
            <Tag variant="drafted">Not in use</Tag>
          </>
        )}
      </div>

      {/* Estimated match */}
      <div className="flex items-center gap-xs px-md py-sm bg-[#FFF4ED] rounded-md border border-[#FDDCBB]">
        <AlertCircle size={18} className="text-[#E5722E]" />
        <span className="text-sm font-semibold text-[#E5722E]">
          Estimated match: {segment.matched_employee_count ?? "\u2014"} Employees
        </span>
      </div>
    </div>

    {/* Footer */}
    <div className="border-t border-greyscale-border mt-lg" />
    <div className="flex items-center justify-between py-lg">
      <Button variant="outline" onClick={onGoBack}>
        Go Back
      </Button>
    </div>
  </div>
);

export default SegmentDetailView;
